import { useCallback } from 'react';
import type { GoodsFirstPageRenderingModel } from '../../../../api/types';
import { bridge } from '../../../bridge';
import { getWebBaseURL } from '../../../getWebBaseURL';
import {
  appendParams,
  buildUrl,
  finalizeUrl,
  getDefaultDeeplink,
} from '../../utils';
import { useExperimentGroup } from '../../../../context/UserContext';
import { isWeb } from '../../../isWeb';

type MoveToGoodsProps = {
  sno: number;
  firstPageRendering: GoodsFirstPageRenderingModel | null;
  loggingAnalytics?: Record<string, unknown>;
  couponSno?: number;
  alwaysRN?: boolean;
};

const DEBUG_MODE = __DEV__;

export const useMoveToGoods = () => {
  const isGoodsDetailRN = useExperimentGroup({
    sno: isWeb ? 'EXP1595' : `EXP1577`,
    targetGroups: ['B'],
  });

  return useCallback(
    ({
      firstPageRendering,
      sno,
      loggingAnalytics,
      couponSno,
      alwaysRN,
    }: MoveToGoodsProps) => {
      const experimentGroup = isGoodsDetailRN || alwaysRN || DEBUG_MODE;
      const getDeeplink = () => {
        if (experimentGroup) {
          const url = getDefaultDeeplink();
          appendParams(url, 'screen', 'GoodsDetail');
          return url;
        }

        return buildUrl(`ably://goods/${sno}`);
      };
      const extras = {
        sno,
        ...(loggingAnalytics ? loggingAnalytics : {}),
        ...(firstPageRendering ? firstPageRendering : {}),
        ...(couponSno ? { coupon_sno: couponSno } : {}),
      };
      const goodsDetailDeeplink = getDeeplink();
      appendParams(goodsDetailDeeplink, 'extras', JSON.stringify(extras));

      return bridge.openDeeplink(finalizeUrl(goodsDetailDeeplink), {
        webCallback: async () => {
          if (experimentGroup) {
            const url = new URL(`${location.origin}/goods/${sno}`);
            const queryString = url.searchParams;
            queryString.set('extras', JSON.stringify(extras));
            location.href = `${location.origin}/goods/${sno}?${queryString}`;
          } else {
            const baseURL = await getWebBaseURL();
            location.href = `${baseURL}/goods/${sno}`;
          }
        },
      });
    },
    [isGoodsDetailRN],
  );
};
